import {
  AlertTriangle,
  Check,
  Info,
  LoaderCircle,
  RotateCcw,
  Sliders
} from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  apiClient,
  type CenaOverrideCompany,
  type CenaValue,
  type CenaScorePoint,
  type CenaWarning
} from "@/shared/api/api-client";
import { foldText } from "@/shared/utils/text-search";
import {
  CENA_BONUS_FLAG,
  CENA_BROADCAST_KIND_LABELS,
  CENA_CHANNEL_LABELS,
  CENA_FIELD_LABELS,
  CENA_GROUP_HELP,
  CENA_GROUP_LABELS,
  CENA_GROUP_ORDER,
  CENA_POINT_TABLE_LABELS,
  cenaFieldsOfGroup
} from "@/shared/vocabulary/cena-labels";
import { approachLabel } from "@/shared/types/mention";
import { TIER_CHOICES } from "@/features/mentions/mention-filter-criteria";

import { errorMessage } from "@/shared/api/http";
import { ApiState } from "@/shared/components/ApiState";
import { SearchSelect } from "@/shared/components/SearchSelect";
import { useConfirmLeave, useUnsavedChanges } from "@/shared/unsaved-changes/UnsavedChanges";
import { AdminPageHeader } from "@/features/admin/AdminPageHeader";
/**
 * Administração → CENA.
 *
 * Os pesos da fórmula eram só leitura: mudar um exigia SQL manual. Aqui o
 * Máster edita a configuração da instalação, a de cada empresa que
 * precisa fugir do padrão, e os pontos das tabelas de lookup.
 *
 * **Editar peso não recalcula nada.** As notas já calculadas ficam como
 * estão — o peso novo vale para uploads novos, relatórios gerados a
 * partir de agora e correções manuais feitas a partir de agora. O banner
 * diz isso na tela, porque é a primeira dúvida de quem mexe aqui.
 *
 * **Override herda tudo ou nada.** A resolução pega UMA linha: existindo
 * configuração própria, a padrão inteira deixa de valer para aquela
 * empresa. Por isso o formulário de criar override nasce preenchido com a
 * padrão — para a herança ser uma cópia explícita, não um acidente.
 */

type Tab = "padrao" | "empresas" | "pontos";

export function WarningList({ warnings }: { warnings: CenaWarning[] }) {
  if (warnings.length === 0) return null;
  return (
    <div className="cena-warnings" role="status">
      <p className="cena-warnings-title">
        <AlertTriangle size={14} aria-hidden="true" /> Salvo, mas confira:
      </p>
      <ul>
        {warnings.map(aviso => (
          <li key={`${aviso.field}:${aviso.message}`}>
            <strong>{CENA_FIELD_LABELS[aviso.field]?.label ?? aviso.field}</strong>{" "}
            {aviso.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Quantos pesos diferem do que está gravado — é o que a barra de
 *  salvar conta. Compara o TEXTO: "2" e "2.0" contam como mudança, que
 *  é o que a pessoa digitou. */
export function changedFields(
  values: Record<string, CenaValue>,
  saved: Record<string, CenaValue> | null
): string[] {
  if (!saved) return [];
  return Object.keys(values).filter(field => (values[field] ?? null) !== (saved[field] ?? null));
}

/**
 * O formulário dos 25 pesos, agrupado pela sequência do cálculo — cada
 * grupo é uma etapa numerada, na ordem em que a fórmula os aplica.
 *
 * O peso neutro tem controle próprio (rádio), e não um campo de número:
 * vazio e zero significam coisas diferentes ali — "menção neutra fica sem
 * nota" contra "menção neutra vale ~5,0" — e um input de número comum
 * colapsaria os dois no primeiro `Number("")`.
 */
export function ConfigForm({
  values,
  saved,
  onChange,
  disabled
}: {
  values: Record<string, CenaValue>;
  /** O que está gravado; o campo diferente ganha destaque. */
  saved: Record<string, CenaValue> | null;
  onChange: (field: string, value: CenaValue) => void;
  disabled: boolean;
}) {
  const changed = new Set(changedFields(values, saved));
  return (
    <>
      {CENA_GROUP_ORDER.map((grupo, index) => (
        <div className="card cena-group-card" key={grupo}>
          <div className="card-title">
            <div>
              <span className="admin-step-num" aria-hidden="true">{index + 1}</span>
              <h3>{CENA_GROUP_LABELS[grupo]}</h3>
              <p>{CENA_GROUP_HELP[grupo]}</p>
            </div>
          </div>
          <div className="cena-field-grid">
            {cenaFieldsOfGroup(grupo).map(campo => {
              const meta = CENA_FIELD_LABELS[campo];
              const isChanged = changed.has(campo);
              if (campo === "fav_neutral") {
                const semNota = values[campo] === null;
                return (
                  <div className={`cena-neutral${isChanged ? " is-changed" : ""}`} key={campo}>
                    <span className="cena-field-label">{meta.label}</span>
                    <div className="cena-choice-group">
                      <label className={`cena-radio${semNota ? " is-checked" : ""}`}>
                        <input
                          type="radio"
                          name="fav_neutral_mode"
                          checked={semNota}
                          disabled={disabled}
                          onChange={() => onChange(campo, null)}
                        />
                        <span>Menção neutra fica sem nota</span>
                      </label>
                      <label className={`cena-radio${semNota ? "" : " is-checked"}`}>
                        <input
                          type="radio"
                          name="fav_neutral_mode"
                          checked={!semNota}
                          disabled={disabled}
                          onChange={() => onChange(campo, "0")}
                        />
                        <span>Menção neutra pontua com peso</span>
                      </label>
                    </div>
                    {!semNota && (
                      <input
                        className="cena-neutral-value"
                        inputMode="decimal"
                        aria-label={`${meta.label}: peso`}
                        value={String(values[campo] ?? "")}
                        disabled={disabled}
                        onChange={event => onChange(campo, event.target.value)}
                      />
                    )}
                    <p className="cena-field-help">{meta.help}</p>
                  </div>
                );
              }
              // Bônus desligável (2026-10-22): o checkbox fica FORA do
              // `<label>` do campo — dentro dele o rótulo do peso passaria
              // a incluir "Conta no CENA" — e, desmarcado, o campo fica
              // desabilitado com o valor preservado.
              const flag = CENA_BONUS_FLAG[campo];
              const ligado = flag ? values[flag] !== false : true;
              const flagChanged = flag ? changed.has(flag) : false;
              const campoEl = (
                <label
                  className={`field cena-field${isChanged ? " is-changed" : ""}`}
                  key={campo}
                  title={isChanged ? `Gravado: ${saved?.[campo] ?? "vazio"}` : undefined}
                >
                  <span className="cena-field-label">{meta.label}</span>
                  <input
                    inputMode="decimal"
                    value={String(values[campo] ?? "")}
                    disabled={disabled || !ligado}
                    onChange={event => onChange(campo, event.target.value)}
                  />
                  <span className="cena-field-help">{meta.help}</span>
                </label>
              );
              if (!flag) return campoEl;
              return (
                <div className="cena-flagged" key={campo}>
                  {campoEl}
                  <label className={`cena-flag${flagChanged ? " is-changed" : ""}`}>
                    <input
                      type="checkbox"
                      aria-label={`Conta no CENA: ${meta.label}`}
                      checked={ligado}
                      disabled={disabled}
                      onChange={event => onChange(flag, event.target.checked)}
                    />
                    <span>Conta no CENA</span>
                  </label>
                  {!ligado && (
                    <span className="cena-flag-note">
                      Desligado: este bônus não soma nos cálculos novos.
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
}

/** A barra de salvar do formulário de pesos: fica presa embaixo da tela
 *  enquanto se rola pelos 25 campos, e diz se há algo por gravar. */
export function CenaSaveBar({
  pending,
  saving,
  onDiscard,
  children
}: {
  pending: number;
  saving: boolean;
  onDiscard: () => void;
  children: ReactNode;
}) {
  return (
    <div className={`admin-savebar${pending ? " is-dirty" : ""}`}>
      <span className="admin-savebar-status" aria-live="polite">
        {pending === 0
          ? "Nenhuma alteração por salvar."
          : `${pending} ${pending === 1 ? "peso alterado" : "pesos alterados"} ainda não ${pending === 1 ? "salvo" : "salvos"}.`}
      </span>
      <div className="admin-savebar-actions">
        {pending > 0 && (
          <button className="text-button" type="button" disabled={saving} onClick={onDiscard}>
            Descartar
          </button>
        )}
        {children}
      </div>
    </div>
  );
}

function DefaultTab({ token }: { token: string }) {
  const [values, setValues] = useState<Record<string, CenaValue> | null>(null);
  const [saved, setSaved] = useState<Record<string, CenaValue> | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [avisos, setAvisos] = useState<CenaWarning[]>([]);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    apiClient
      .getCenaConfig(token)
      .then(config => {
        setValues(config.values);
        setSaved(config.values);
      })
      .catch(error =>
        setErro(errorMessage(error, "Não foi possível carregar."))
      );
  }, [token]);

  const salvar = async (): Promise<boolean> => {
    if (!values) return false;
    setSalvando(true);
    setErro(null);
    try {
      const resultado = await apiClient.saveCenaConfig(token, values);
      setValues(resultado.config.values);
      setSaved(resultado.config.values);
      setAvisos(resultado.warnings);
      return true;
    } catch (error) {
      setErro(errorMessage(error, "Não foi possível salvar."));
      return false;
    } finally {
      setSalvando(false);
    }
  };

  // Sair com peso alterado e não salvo pergunta antes (`shared/unsaved-changes`).
  useUnsavedChanges(
    values !== null && changedFields(values, saved).length > 0,
    salvar,
    "os pesos padrão do CENA"
  );

  if (erro && !values) return <p className="form-error" role="alert">{erro}</p>;
  if (!values) return <ApiState kind="loading">Carregando pesos…</ApiState>;

  return (
    <div className="ai-admin-stack">
      {erro && <p className="form-error" role="alert">{erro}</p>}
      <WarningList warnings={avisos} />
      <ConfigForm
        values={values}
        saved={saved}
        disabled={salvando}
        onChange={(campo, valor) => setValues({ ...values, [campo]: valor })}
      />
      <CenaSaveBar
        pending={changedFields(values, saved).length}
        saving={salvando}
        onDiscard={() => saved && setValues(saved)}
      >
        <button
          className="primary-button"
          type="button"
          disabled={salvando}
          onClick={() => void salvar()}
        >
          {salvando ? <LoaderCircle className="spin" size={16} /> : <Check size={16} />}{" "}
          Salvar pesos
        </button>
      </CenaSaveBar>
    </div>
  );
}

const rotuloEmpresa = (empresa: CenaOverrideCompany) =>
  empresa.has_override ? `${empresa.name} · configuração própria` : empresa.name;

function CompaniesTab({ token }: { token: string }) {
  const [empresas, setEmpresas] = useState<CenaOverrideCompany[]>([]);
  const [selecionada, setSelecionada] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, CenaValue> | null>(null);
  const [saved, setSaved] = useState<Record<string, CenaValue> | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [avisos, setAvisos] = useState<CenaWarning[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  // Toda empresa que já passou pela tela (a lista inicial e cada
  // busca), pelo id: é de onde sai o NOME da escolhida quando ela veio
  // de uma busca e não está na lista de quem tem configuração própria.
  const [conhecidas, setConhecidas] = useState<Record<string, CenaOverrideCompany>>({});
  // O que a tela abriu (a configuração própria, ou a cópia da padrão pra
  // quem não tem): a base do "há alteração não salva". `saved` não serve
  // pra cópia — nada está gravado, e a barra conta zero de propósito.
  const [aberto, setAberto] = useState<Record<string, CenaValue> | null>(null);
  const confirmLeave = useConfirmLeave();

  const lembrar = (lista: CenaOverrideCompany[]) =>
    setConhecidas(atual => {
      const proximo = { ...atual };
      for (const empresa of lista) proximo[empresa.company_id] = empresa;
      return proximo;
    });

  // Sem `q`, a rota devolve as com configuração própria MAIS as
  // primeiras 50 das outras pelo nome — é a lista que a caixa mostra ao
  // abrir (até 2026-10-21 vinham só as com configuração própria, e a
  // caixa abria vazia quando não havia nenhuma). É ela também que diz
  // se a escolhida tem configuração própria (por isso é recarregada
  // depois de salvar e de voltar ao padrão).
  const carregarLista = async () => {
    try {
      const lista = await apiClient.listCenaOverrides(token);
      setEmpresas(lista);
      lembrar(lista);
    } catch (error) {
      setErro(errorMessage(error, "Não foi possível carregar."));
    } finally {
      setCarregandoLista(false);
    }
  };

  // A busca da caixa: acha qualquer empresa da instalação pelo nome
  // (12 mil no devstack — é por isso que a lista inteira nunca vem). A
  // rota devolve as com configuração própria MAIS o que casou; a caixa
  // mostra só o que casou, senão digitar "amil" listava 317 overrides
  // na frente da AMIL (medido no devstack).
  const buscarEmpresa = useCallback(
    async (q: string) => {
      const lista = await apiClient.listCenaOverrides(token, q);
      lembrar(lista);
      const termo = foldText(q);
      return lista.filter(empresa => foldText(empresa.name).includes(termo)).map(empresa => ({
        value: empresa.company_id,
        label: rotuloEmpresa(empresa)
      }));
    },
    [token]
  );

  useEffect(() => {
    void carregarLista();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Descarte por CARGA: escolher A (lenta) e logo B (rápida) deixava B
  // selecionada com os pesos de A quando A chegava por último — e "Salvar
  // pesos" gravava os de A na empresa B. Só a abertura mais recente
  // escreve na tela, falha incluída (2026-10-09, teste de render). Mesma
  // forma do `SearchSelect` e da lista de menções.
  const aberturaAtual = useRef(0);

  const abrir = async (company: CenaOverrideCompany) => {
    const abertura = ++aberturaAtual.current;
    setSelecionada(company.company_id);
    setAvisos([]);
    setErro(null);
    setValues(null);
    setSaved(null);
    setAberto(null);
    try {
      if (company.has_override) {
        const config = await apiClient.getCenaOverride(token, company.company_id);
        if (abertura !== aberturaAtual.current) return;
        setValues(config.values);
        setSaved(config.values);
        setAberto(config.values);
      } else {
        // Nasce como CÓPIA da padrão: override herda tudo ou nada, e a
        // cópia explícita é o que impede alguém achar que só o campo
        // editado passa a valer.
        const padrao = await apiClient.getCenaConfig(token);
        if (abertura !== aberturaAtual.current) return;
        // Sem `saved`: nada está gravado pra esta empresa ainda, então a
        // cópia inteira é "por criar", não "alterada".
        setValues(padrao.values);
        setAberto(padrao.values);
      }
    } catch (error) {
      if (abertura !== aberturaAtual.current) return;
      setErro(errorMessage(error, "Não foi possível abrir."));
    }
  };

  const salvar = async (): Promise<boolean> => {
    if (!selecionada || !values) return false;
    setSalvando(true);
    setErro(null);
    try {
      const resultado = await apiClient.saveCenaOverride(token, selecionada, values);
      setValues(resultado.config.values);
      setSaved(resultado.config.values);
      setAberto(resultado.config.values);
      setAvisos(resultado.warnings);
      await carregarLista();
      return true;
    } catch (error) {
      setErro(errorMessage(error, "Não foi possível salvar."));
      return false;
    } finally {
      setSalvando(false);
    }
  };

  const voltarAoPadrao = async () => {
    if (!selecionada) return;
    setSalvando(true);
    setErro(null);
    try {
      await apiClient.clearCenaOverride(token, selecionada);
      setSelecionada(null);
      setValues(null);
      await carregarLista();
    } catch (error) {
      setErro(errorMessage(error, "Não foi possível remover."));
    } finally {
      setSalvando(false);
    }
  };

  // Trocar de empresa com peso alterado e não salvo pergunta antes, como
  // sair da tela (`shared/unsaved-changes`).
  useUnsavedChanges(
    values !== null && aberto !== null && changedFields(values, aberto).length > 0,
    salvar,
    "os pesos do CENA desta empresa"
  );
  const trocarPara = (company: CenaOverrideCompany) => confirmLeave(() => void abrir(company));

  if (carregandoLista) return <ApiState kind="loading">Carregando empresas…</ApiState>;

  const atual = selecionada ? conhecidas[selecionada] : undefined;
  // A lista sem `q` é recarregada depois de salvar e de limpar, e é ela
  // que decide o botão "Voltar ao padrão" — `conhecidas` guarda o nome,
  // mas o `has_override` de lá envelhece.
  const temPropria = empresas.some(e => e.company_id === selecionada && e.has_override);
  // As que já foram editadas, listadas abaixo da caixa (pedido de
  // 2026-10-21): a caixa mostra uma empresa por vez, e sem esta lista
  // não havia como ver de relance QUAIS fogem da padrão.
  const comPropria = empresas.filter(e => e.has_override);

  return (
    <div className="ai-admin-stack">
      {erro && <p className="form-error" role="alert">{erro}</p>}
      <div className="card">
        <div className="card-title">
          <div>
            <Sliders size={18} aria-hidden="true" />
            <h3>Configuração por empresa</h3>
            <p>
              Empresa com configuração própria ignora a padrão POR INTEIRO — não é
              mistura das duas. Quem não tem, usa a padrão da instalação.
            </p>
          </div>
        </div>
        {/*
          A caixa abre com as que TÊM configuração própria e, depois, as
          primeiras pelo nome; digitar busca qualquer empresa da
          instalação. Era um campo de busca + um select separados; virou
          a escolha com busca da casa (`SearchSelect`), que é a mesma da
          tela de consumo de IA.
        */}
        <div className="cena-company-pick">
          <SearchSelect
            label="Empresa"
            allLabel="Escolha uma empresa…"
            value={selecionada ?? ""}
            valueLabel={atual?.name}
            options={empresas.map(empresa => ({
              value: empresa.company_id,
              label: rotuloEmpresa(empresa)
            }))}
            search={buscarEmpresa}
            onChange={id => {
              const empresa = conhecidas[id];
              if (empresa) trocarPara(empresa);
              else
                confirmLeave(() => {
                  setSelecionada(null);
                  setValues(null);
                });
            }}
          />
        </div>
        <div className="cena-override-list">
          <p className="cena-override-list-title">
            Com configuração própria ({comPropria.length})
          </p>
          {comPropria.length === 0 ? (
            <p className="ai-admin-caption">
              Nenhuma empresa tem configuração própria — todas usam a padrão da instalação.
            </p>
          ) : (
            <table className="ai-admin-table">
              <tbody>
                {comPropria.map(empresa => (
                  <tr
                    key={empresa.company_id}
                    tabIndex={0}
                    aria-selected={selecionada === empresa.company_id}
                    className={selecionada === empresa.company_id ? "is-selected" : undefined}
                    onClick={() => trocarPara(empresa)}
                    onKeyDown={event => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        trocarPara(empresa);
                      }
                    }}
                  >
                    <td>{empresa.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selecionada && values && (
        <>
          {!temPropria && (
            <p className="cena-notice">
              <Info size={14} aria-hidden="true" /> Esta empresa usa a configuração
              padrão. Os valores abaixo são uma cópia dela — salvar cria uma
              configuração própria, e a partir daí mudanças no padrão não a alcançam.
            </p>
          )}
          <WarningList warnings={avisos} />
          <ConfigForm
            values={values}
            saved={saved}
            disabled={salvando}
            onChange={(campo, valor) => setValues({ ...values, [campo]: valor })}
          />
          <CenaSaveBar
            pending={changedFields(values, saved).length}
            saving={salvando}
            onDiscard={() => saved && setValues(saved)}
          >
            {temPropria && (
              <button
                className="secondary-button"
                type="button"
                disabled={salvando}
                onClick={() => void voltarAoPadrao()}
              >
                <RotateCcw size={16} aria-hidden="true" /> Voltar ao padrão
              </button>
            )}
            <button
              className="primary-button"
              type="button"
              disabled={salvando}
              onClick={() => void salvar()}
            >
              {salvando ? <LoaderCircle className="spin" size={16} /> : <Check size={16} />}{" "}
              {temPropria ? "Salvar" : "Criar configuração própria"}
            </button>
          </CenaSaveBar>
        </>
      )}
    </div>
  );
}

// O servidor manda as chaves cruas coladas ("print_online · P",
// "protagonist"); o rótulo de tela sai daqui, com os vocabulários que a
// tela de menções já usa. Chave desconhecida fica com o rótulo do servidor.
function pointLabel(ponto: CenaScorePoint): string {
  const [primeira = "", segunda] = ponto.key;
  switch (ponto.table) {
    case "format": {
      const tipo = CENA_BROADCAST_KIND_LABELS[primeira];
      return tipo && segunda ? `${tipo} (${segunda})` : ponto.label;
    }
    case "approach": {
      const rotulo = approachLabel(primeira);
      return rotulo !== primeira ? rotulo : ponto.label;
    }
    case "vehicle":
      return TIER_CHOICES.find(tier => tier.value === primeira)?.label ?? ponto.label;
    case "channel":
      return CENA_CHANNEL_LABELS[primeira] ?? ponto.label;
    default:
      return ponto.label;
  }
}

function PointsTab({ token }: { token: string }) {
  const [pontos, setPontos] = useState<CenaScorePoint[]>([]);
  const [rascunho, setRascunho] = useState<Record<string, string>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);

  const carregar = async () => {
    try {
      setPontos(await apiClient.listCenaPoints(token));
    } catch (error) {
      setErro(errorMessage(error, "Não foi possível carregar."));
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const chave = (ponto: CenaScorePoint) => `${ponto.table}:${ponto.key.join("~")}`;

  const salvar = async (ponto: CenaScorePoint) => {
    const id = chave(ponto);
    const valor = rascunho[id];
    if (valor === undefined) return;
    setSalvando(id);
    setErro(null);
    try {
      await apiClient.saveCenaPoint(token, ponto.table, ponto.key, valor);
      setRascunho(anterior => {
        const copia = { ...anterior };
        delete copia[id];
        return copia;
      });
      await carregar();
    } catch (error) {
      setErro(errorMessage(error, "Não foi possível salvar."));
    } finally {
      setSalvando(null);
    }
  };

  // Sair com ponto editado e não salvo pergunta antes; "Salvar e sair"
  // grava cada linha editada, como o "Salvar" de cada uma faria.
  const salvarTodos = async (): Promise<boolean> => {
    setErro(null);
    try {
      for (const ponto of pontos) {
        const id = chave(ponto);
        const valor = rascunho[id];
        if (valor === undefined) continue;
        await apiClient.saveCenaPoint(token, ponto.table, ponto.key, valor);
        setRascunho(anterior => {
          const copia = { ...anterior };
          delete copia[id];
          return copia;
        });
      }
      return true;
    } catch (error) {
      setErro(errorMessage(error, "Não foi possível salvar."));
      return false;
    } finally {
      await carregar();
    }
  };
  useUnsavedChanges(Object.keys(rascunho).length > 0, salvarTodos, "as tabelas de pontos do CENA");

  if (carregando) return <ApiState kind="loading">Carregando pontos…</ApiState>;

  const tabelas = [...new Set(pontos.map(p => p.table))];

  return (
    <div className="ai-admin-stack">
      {erro && <p className="form-error" role="alert">{erro}</p>}
      {tabelas.map(tabela => (
        <div className="card" key={tabela}>
          <div className="card-title">
            <div>
              <Sliders size={18} aria-hidden="true" />
              <h3>{CENA_POINT_TABLE_LABELS[tabela] ?? tabela}</h3>
              <p>
                Os pontos crus, antes de serem multiplicados pelos pesos. Só o valor é
                editável — as linhas vêm de vocabulário fechado. Cada linha salva sozinha.
              </p>
            </div>
          </div>
          <table className="ai-admin-table">
            <thead>
              <tr>
                <th>Item</th>
                <th className="cena-points-col">Pontos</th>
                <th aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {pontos
                .filter(ponto => ponto.table === tabela)
                .map(ponto => {
                  const id = chave(ponto);
                  const editado = rascunho[id] !== undefined;
                  return (
                    <tr key={id} className={editado ? "is-dirty" : undefined}>
                      <td>{pointLabel(ponto)}</td>
                      <td>
                        <input
                          className="cena-point-input"
                          inputMode="decimal"
                          value={rascunho[id] ?? ponto.point}
                          onChange={event =>
                            setRascunho({ ...rascunho, [id]: event.target.value })
                          }
                        />
                      </td>
                      <td className="ai-admin-row-actions">
                        {editado && (
                          <button
                            className="text-button"
                            type="button"
                            disabled={salvando === id}
                            onClick={() =>
                              setRascunho(anterior => {
                                const copia = { ...anterior };
                                delete copia[id];
                                return copia;
                              })
                            }
                          >
                            Desfazer
                          </button>
                        )}{" "}
                        <button
                          className="secondary-button"
                          type="button"
                          disabled={!editado || salvando === id}
                          onClick={() => void salvar(ponto)}
                        >
                          {salvando === id ? (
                            <LoaderCircle className="spin" size={14} />
                          ) : (
                            <Check size={14} />
                          )}{" "}
                          Salvar
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

export function CenaAdmin({ token }: { token: string }) {
  const [tab, setTab] = useState<Tab>("padrao");
  const confirmLeave = useConfirmLeave();

  return (
    <div className="copiloto-admin ajustes-panel admin-panel">
      <AdminPageHeader title="CENA" description="Os pesos que transformam uma menção em nota de 0 a 10." />

      <p className="cena-notice cena-notice-strong">
        <Info size={14} aria-hidden="true" /> Alterar pesos <strong>não recalcula</strong>{" "}
        menções nem amostras já existentes. Vale para uploads novos, amostras
        geradas a partir de agora e correções manuais feitas a partir de agora.
      </p>

      <nav className="copiloto-admin-tabs" aria-label="Seções da configuração do CENA">
        <button
          className={tab === "padrao" ? "active" : undefined}
          type="button"
          onClick={() => confirmLeave(() => setTab("padrao"))}
        >
          Padrão da instalação
        </button>
        <button
          className={tab === "empresas" ? "active" : undefined}
          type="button"
          onClick={() => confirmLeave(() => setTab("empresas"))}
        >
          Por empresa
        </button>
        <button
          className={tab === "pontos" ? "active" : undefined}
          type="button"
          onClick={() => confirmLeave(() => setTab("pontos"))}
        >
          Tabelas de pontos
        </button>
      </nav>

      {tab === "padrao" && <DefaultTab token={token} />}
      {tab === "empresas" && <CompaniesTab token={token} />}
      {tab === "pontos" && <PointsTab token={token} />}
    </div>
  );
}
